import { h, Component, createRef } from 'preact';
import { createPortal } from 'preact/compat';
import { cx } from 'react-emotion';
import { withContext } from 'common/context';
import { isMobile } from 'mobile-device-detect';
import { getButtonProps } from '../../common/props';
import Button from '../Button/Button';
import Image from '../Image/Image';
import PopupZoomButtons from './PopupZoomButtons';
import PopupFocusLock from './PopupFocusLock';
import { styles } from './styles';
import { getDimensions, getOriginalAssetInfo, getValidSteps, initPanzoom } from './helpers';

class ZoomPopup extends Component {
  state = {
    highResLoaded: false,
    steps: 0,
    currentStep: 0
  };

  popupTransformation = {
    ...this.props.context.config.selectTransformation(),
    ...(this.props.asset && this.props.asset.transformation),
    crop: 'limit'
  };

  zoomWrap = createRef();

  async componentDidMount() {
    this.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const { level, steps } = this.props;
    const logErrors = this.props.context.config.selectLogErrors();
    // Get original image info
    let assetInfo;
    let validSteps;
    try {
      assetInfo = await getOriginalAssetInfo(this.props);
    } catch (error) {
      if (logErrors) console.error(error);
    }
    if (assetInfo && assetInfo.input) {
      const assetWidth = assetInfo && assetInfo.input && assetInfo.input.width;

      // Calc maxAvailableZoom
      validSteps = getValidSteps(level, steps, assetWidth);
      this.setSteps(validSteps);

      if (logErrors && steps > 0 && validSteps === 0) {
        console.warn(
          `Original asset size does not allow for extra zoom levels. [asset width: ${assetWidth}px]`
        );
      }
    }
    if (this.zoomWrap && this.zoomWrap.current) {
      this.panzoom = initPanzoom(
        this.zoomWrap.current,
        level,
        this.props.stepLimit && validSteps !== undefined ? validSteps : steps
      );
    }
  }

  componentWillUnmount() {
    document.body.style.overflow = this.bodyOverflow;
  }

  close = e => {
    if (this.props.onClose) {
      this.props.onClose(e);
    }
  };

  zoomIn = () => {
    const nextStep = this.state.currentStep + 1;
    this.setState({ currentStep: nextStep });
    this.panzoom.zoom(Math.pow(this.props.level, nextStep), { animate: true });
  };

  zoomOut = () => {
    const nextStep = this.state.currentStep - 1;
    this.setState({ currentStep: nextStep });
    if (nextStep === 0) {
      this.panzoom.reset();
    } else {
      this.panzoom.zoom(Math.pow(this.props.level, nextStep), { animate: true });
    }
  };

  renderImage = props => {
    return (
      <Image
        className={props.className}
        mediaType="image"
        resourceType="image"
        publicId={this.props.asset && this.props.asset.publicId}
        transformation={this.popupTransformation}
        active
        width={props.width}
        height={props.height}
        onLoad={props.onLoad}
        alt={this.props.alt}
      />
    );
  };

  setSteps = steps => {
    // state.steps holds the number of zoom steps that will not cause
    // the original image to be stretched.
    // use it to prevent zoom levels that exceeds original image dimensions
    this.setState({ steps });
  };

  render(props, state) {
    const dims = getDimensions();
    const classes = styles({ ...props, ...dims });
    const maxZoom = Math.pow(this.props.level, this.props.steps);
    const maxAvailableZoom = Math.pow(this.props.level, this.state.steps || 0);
    // if stepLimit - limit to available zoom (lossless), otherwise allow up to props.steps.
    const zoomSteps = this.props.stepLimit ? state.steps : props.steps;

    return createPortal(
      <div role="dialog" data-test="popup" className={classes.popup}>
        <PopupFocusLock>
          <Button
            {...getButtonProps(props)}
            icon="close"
            className={classes.close}
            onClick={this.close}
          />

          <div
            ref={maxZoom > 1 ? this.zoomWrap : undefined}
            className={cx(
              {
                'hi-res': state.highResLoaded,
                loading: maxAvailableZoom > 1 && !state.highResLoaded
              },
              classes.zoomWrap
            )}
          >
            {this.renderImage({
              className: 'zoom-image',
              width: dims.width,
              height: dims.height
            })}
            {maxAvailableZoom > 1 &&
              this.renderImage({
                className: 'multi-zoom-image',
                width: dims.width * maxAvailableZoom,
                height: dims.height * maxAvailableZoom,
                onLoad: () => this.setState({ highResLoaded: true })
              })}
          </div>

          {maxZoom > 1 && !isMobile && zoomSteps > 0 && (
            <PopupZoomButtons
              {...props.context.config.selectNavigationButtonProps()}
              currentStep={state.currentStep}
              steps={zoomSteps}
              zoomIn={this.zoomIn}
              zoomOut={this.zoomOut}
            />
          )}
        </PopupFocusLock>
      </div>,
      document.body
    );
  }
}

export default withContext(ZoomPopup);
